// src/pages/settings/McpPage.tsx
//
// MCP — /settings/mcp (Reforma UX §6.11, MCP-01..03).
// Extraída da antiga SettingsPage (aba `mcp`), reorganizada em 2 abas:
//
// - **Plugin** (MCP-02): seletor de runtime de destino (OpenClaw · Hermes ·
//   MC Lean) + seletor de token a embutir → snippet JSON onde o token aparece
//   como `<nome>` (NUNCA o valor). "Copiar instalador" busca o valor real via
//   GET /v1/mcp-tokens/:id/secret e leva o JSON completo pro clipboard.
// - **Tokens** (MCP-03): gerar (modal nome + runtime de destino; o valor é
//   exibido UMA única vez), listar (nome · runtime · prefixo · último uso),
//   copiar valor completo, excluir com confirmação.
//
// "Runtime de destino" persiste no campo `client_type` do POST /v1/mcp-tokens
// (não existe coluna dedicada de runtime no backend — client_type cumpre o papel).

import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import type { McpTokenItem } from '@/lib/api';
import {
  Trash2,
  Loader2,
  Plus,
  KeyRound,
  Copy,
  Check,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Alert } from '@/components/ui/Alert';
import '@/styles/wf-agents.css'; // chrome: .dhead/.dnewbtn
import '@/styles/wf-settings.css';

/** URL canônica do MCP server do RAIOS (mesma da antiga SettingsPage). */
const MCP_URL = 'https://raios.app/api/v1/mcp';

/** Runtimes de destino do wireframe §12 (Plugin + modal de token). */
const MCP_RUNTIMES = [
  { value: 'openclaw', label: 'OpenClaw' },
  { value: 'hermes', label: 'Hermes' },
  // Breno 12/06: "MC Lean" não existe (veio do wireframe por engano). E o
  // instalador é o comando `claude mcp add` — específico do CLAUDE CODE, não
  // de CLI genérico; o nome diz exatamente o que é (value 'claude-code' já é
  // client_type real no banco).
  { value: 'claude-code', label: 'Claude Code' },
  // Vercel: NÃO é um cliente MCP interativo — é uma função serverless que CHAMA o
  // endpoint MCP do RAIOS server-to-server (ex: o funil Delegação Infinita persistindo
  // o lead via person_upsert). O "instalador" é config de env var, não `claude mcp add`.
  { value: 'vercel', label: 'Vercel' },
] as const;

/** Label amigável pro client_type — cobre runtimes novos e legados. */
const CLIENT_TYPE_LABELS: Record<string, string> = {
  openclaw: 'OpenClaw',
  hermes: 'Hermes',
  'claude-code': 'Claude Code',
  vercel: 'Vercel',
  // legados (tokens antigos no banco)
  cli: 'CLI',
  codex: 'Codex',
  cursor: 'Cursor',
  mclean: 'MC Lean',
  other: 'Outro',
};

function runtimeLabel(clientType: string): string {
  return CLIENT_TYPE_LABELS[clientType] ?? clientType;
}

/** Snippet do instalador POR RUNTIME (Breno 12/06). `tokenText` é `<nome>` na
 *  exibição e o valor real na cópia.
 *  - openclaw: JSON com a chave `transport` (formato da config do OpenClaw).
 *  - claude-code: COMANDO `claude mcp add` (formato do
 *    instalador pré-refatoração, recuperado do git history da SettingsPage;
 *    o comando grava no ~/.claude.json a mesma entrada `type/url/headers`
 *    verificada na config real desta máquina).
 *  - hermes: JSON padrão MCP (`type`); formato canônico do Hermes a confirmar
 *    (fonte primária indisponível — árvore vivia em /tmp). */
function buildInstallerSnippet(runtime: string, tokenText: string): string {
  if (runtime === 'vercel') {
    // Vercel NÃO "instala" um servidor MCP — a função serverless CHAMA o endpoint MCP
    // do RAIOS server-to-server. O "instalador" é configurar os env vars do projeto
    // (RAIOS_MCP_URL + RAIOS_MCP_TOKEN) que o código do funil já lê; depois redeploy.
    return [
      '# Vercel — o funil NÃO instala um servidor MCP; ele CHAMA o MCP do RAIOS server-to-server.',
      '# "Instalar" aqui = configurar os env vars do projeto na Vercel. Dois modos:',
      '',
      '# ── Opção A: Vercel CLI (no diretório do projeto) ──',
      `printf '%s' "${MCP_URL}" | vercel env add RAIOS_MCP_URL production`,
      `printf '%s' "${tokenText}" | vercel env add RAIOS_MCP_TOKEN production`,
      "# (opcional — nome da tool, default 'person_upsert')",
      "# printf '%s' 'person_upsert' | vercel env add RAIOS_MCP_PERSON_UPSERT_TOOL production",
      'vercel deploy --prod   # redeploy pra aplicar os env vars',
      '',
      '# ── Opção B: Dashboard da Vercel ──',
      '# Project → Settings → Environment Variables → adicione (Production):',
      `#   RAIOS_MCP_URL   = ${MCP_URL}`,
      `#   RAIOS_MCP_TOKEN = ${tokenText}`,
      '#   (opcional) RAIOS_MCP_PERSON_UPSERT_TOOL = person_upsert',
      '# Depois: Deployments → Redeploy.',
    ].join('\n');
  }
  if (runtime === 'claude-code') {
    // PROMPT auto-instalável (Breno 12/06): o usuário cola NO Claude Code e o
    // próprio agente instala o MCP + o espelhamento de conversa (script
    // canônico servido pelo RAIOS — sem segredos; o token vem só no comando 1).
    return [
      'Instale o MCP do RAIOS nesta máquina e configure o espelhamento da conversa. Faça exatamente isto:',
      '',
      '1) Registre o servidor MCP:',
      `   claude mcp add --transport http raios ${MCP_URL} \\`,
      `     --header "Authorization: Bearer ${tokenText}"`,
      '',
      '2) Instale o hook oficial de espelhamento:',
      '   mkdir -p ~/.claude/hooks && curl -fsSL https://raios.app/api/v1/public/mcp/mirror-hook.sh -o ~/.claude/hooks/raios-mirror.sh && chmod +x ~/.claude/hooks/raios-mirror.sh',
      '',
      '3) No ~/.claude/settings.json, faça MERGE (preservando todo o conteúdo existente) adicionando os hooks:',
      '   - hooks.UserPromptSubmit: {"type":"command","command":"~/.claude/hooks/raios-mirror.sh prompt","async":true,"timeout":10}',
      '   - hooks.Stop:             {"type":"command","command":"~/.claude/hooks/raios-mirror.sh stop","async":true,"timeout":10}',
      '',
      '4) Valide: rode `echo \'{"prompt":"[setup RAIOS] espelhamento ativo"}\' | ~/.claude/hooks/raios-mirror.sh prompt` e depois chame a tool MCP session_ping. Me confirme que a sessão "MCP · …" apareceu na tela Sessões do RAIOS.',
    ].join('\n');
  }
  const server =
    runtime === 'openclaw'
      ? { transport: 'http', url: MCP_URL, headers: { Authorization: `Bearer ${tokenText}` } }
      : { type: 'http', url: MCP_URL, headers: { Authorization: `Bearer ${tokenText}` } };
  return JSON.stringify({ mcpServers: { raios: server } }, null, 2);
}

/** Dica de instalação por runtime (onde colar/rodar o snippet). */
const RUNTIME_HINTS: Record<string, string> = {
  openclaw: 'Cole na configuração de MCP servers do OpenClaw.',
  hermes: 'Cole na configuração de MCP servers do agente Hermes (formato canônico a confirmar).',
  'claude-code': 'Cole esta instrução numa conversa do Claude Code — ele instala o MCP e o espelhamento de conversa sozinho (o espelhamento exige o restart do RAIOS que publicou o script).',
  vercel: 'Rode os comandos no projeto Vercel (ou configure os env vars no dashboard) — não instala servidor MCP; só aponta a função serverless pro endpoint MCP do RAIOS. Requer redeploy.',
};

export function McpPage() {
  const [tab, setTab] = useState<string>('plugin');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // --- MCP tokens (F4 — MCP Server) ---
  const [mcpTokens, setMcpTokens] = useState<McpTokenItem[]>([]);
  const [mcpLoading, setMcpLoading] = useState(true);
  const [mcpCopiedId, setMcpCopiedId] = useState<string | null>(null);
  const [mcpRevokingId, setMcpRevokingId] = useState<string | null>(null);
  const [revokeConfirmId, setRevokeConfirmId] = useState<string | null>(null);

  // --- Aba Plugin (MCP-02) ---
  const [installerRuntime, setInstallerRuntime] = useState<string>(MCP_RUNTIMES[0].value);
  const [installerTokenId, setInstallerTokenId] = useState<string>('');
  const [installerCopying, setInstallerCopying] = useState(false);
  const [installerCopied, setInstallerCopied] = useState(false);

  // --- Modal "Gerar novo token" (MCP-03) ---
  const [genModalOpen, setGenModalOpen] = useState(false);
  const [mcpGenLabel, setMcpGenLabel] = useState('');
  const [mcpGenRuntime, setMcpGenRuntime] = useState<string>(MCP_RUNTIMES[0].value);
  const [mcpGenerating, setMcpGenerating] = useState(false);
  // Token recém-criado — exibido UMA única vez dentro do modal.
  const [mcpJustCreated, setMcpJustCreated] = useState<{ label: string; token: string } | null>(null);

  const fetchMcpTokens = useCallback(async () => {
    try {
      const data = await api.listMcpTokens();
      setMcpTokens(data.filter((t) => !t.revoked_at));
    } catch { /* */ } finally { setMcpLoading(false); }
  }, []);

  useEffect(() => { fetchMcpTokens(); }, [fetchMcpTokens]);

  // Mantém o seletor de token do Plugin apontando pra um token válido.
  useEffect(() => {
    if (mcpTokens.length === 0) {
      setInstallerTokenId('');
      return;
    }
    if (!installerTokenId || !mcpTokens.some((t) => t.id === installerTokenId)) {
      setInstallerTokenId(mcpTokens[0].id);
    }
  }, [mcpTokens, installerTokenId]);

  const installerToken = mcpTokens.find((t) => t.id === installerTokenId) ?? null;
  const installerRuntimeLabel = MCP_RUNTIMES.find((r) => r.value === installerRuntime)?.label ?? installerRuntime;

  function openGenModal() {
    setMcpGenLabel('');
    setMcpGenRuntime(MCP_RUNTIMES[0].value);
    setMcpJustCreated(null);
    setGenModalOpen(true);
  }

  function closeGenModal() {
    setGenModalOpen(false);
    // O valor só é exibido uma vez — descarta ao fechar.
    setMcpJustCreated(null);
  }

  async function handleGenerateMcp() {
    const label = mcpGenLabel.trim() || `${mcpGenRuntime} token`;
    setMcpGenerating(true);
    try {
      // "Runtime de destino" → client_type (campo aceito pelo POST /v1/mcp-tokens).
      const created = await api.createMcpToken({ label, client_type: mcpGenRuntime });
      setMcpJustCreated({ label: created.label, token: created.token_plaintext });
      setMcpGenLabel('');
      await fetchMcpTokens();
    } catch (err) {
      setFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Erro ao gerar token.' });
    } finally { setMcpGenerating(false); }
  }

  async function handleCopyMcp(id: string, value?: string) {
    try {
      const token = value ?? (await api.getMcpTokenSecret(id));
      await navigator.clipboard.writeText(token);
      setMcpCopiedId(id);
      setTimeout(() => setMcpCopiedId((c) => (c === id ? null : c)), 1800);
    } catch (err) {
      setFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Erro ao copiar token.' });
    }
  }

  async function handleRevokeMcp(id: string) {
    setMcpRevokingId(id);
    setRevokeConfirmId(null);
    try {
      await api.revokeMcpToken(id);
      await fetchMcpTokens();
    } catch (err) {
      setFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Erro ao revogar token.' });
    } finally { setMcpRevokingId(null); }
  }

  /** MCP-02 — copia o instalador com o token REAL (GET /:id/secret) no clipboard. */
  async function handleCopyInstaller() {
    if (!installerToken) return;
    setInstallerCopying(true);
    try {
      const secret = await api.getMcpTokenSecret(installerToken.id);
      await navigator.clipboard.writeText(buildInstallerSnippet(installerRuntime, secret));
      setInstallerCopied(true);
      setTimeout(() => setInstallerCopied(false), 2000);
    } catch (err) {
      setFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Erro ao copiar instalador.' });
    } finally {
      setInstallerCopying(false);
    }
  }

  const revokeConfirmToken = revokeConfirmId
    ? mcpTokens.find((t) => t.id === revokeConfirmId) ?? null
    : null;

  return (
    <div>
      <PageHeader
        title="MCP"
        description="Conexão de runtimes externos ao RAIOS via MCP: gere o instalador com o token embutido e gerencie os tokens."
      />

      {feedback && (
        <Alert
          variant={feedback.type === 'success' ? 'success' : 'destructive'}
          className="mt-4"
          dismissible
          onDismiss={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      )}

      <div className="mt-4">
        {/* Abas Plugin · Tokens (wireframe §12 .ctabs) */}
        <div className="ctabs">
          <button
            type="button"
            className={tab === 'plugin' ? 'on' : undefined}
            onClick={() => setTab('plugin')}
          >
            Plugin
          </button>
          <button
            type="button"
            className={tab === 'tokens' ? 'on' : undefined}
            onClick={() => setTab('tokens')}
          >
            Tokens
          </button>
        </div>

        <div>
          {/* ─── Aba Plugin (MCP-02 — wireframe .mcp-install/.code-box) ── */}
          {tab === 'plugin' && (
            <div>
              {mcpLoading ? (
                <Skeleton className="h-64 rounded-xl" />
              ) : mcpTokens.length === 0 ? (
                <Card className="p-6">
                  <EmptyState
                    icon={KeyRound}
                    title="Nenhum token MCP"
                    description="Gere um token na aba Tokens para montar o instalador."
                    action={{ label: 'Gerar novo token', onClick: () => { setTab('tokens'); openGenModal(); } }}
                  />
                </Card>
              ) : (
                <div className="mcp-install">
                  <div className="mcp-sels">
                    <div className="wf-field" style={{ margin: 0 }}>
                      <label className="wf-label" htmlFor="mcp-runtime">
                        Runtime de destino
                      </label>
                      <select
                        id="mcp-runtime"
                        className="wf-select"
                        value={installerRuntime}
                        onChange={(e) => { setInstallerRuntime(e.target.value); setInstallerCopied(false); }}
                      >
                        {MCP_RUNTIMES.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="wf-field" style={{ margin: 0 }}>
                      <label className="wf-label" htmlFor="mcp-token">
                        Token a embutir
                      </label>
                      <select
                        id="mcp-token"
                        className="wf-select"
                        value={installerTokenId}
                        onChange={(e) => { setInstallerTokenId(e.target.value); setInstallerCopied(false); }}
                      >
                        {mcpTokens.map((t) => (
                          <option key={t.id} value={t.id}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Snippet (wireframe .code-box) — token NUNCA exibido; só o
                      nome entre <...>, destacado em âmbar (.tk) */}
                  <div className="code-box">
                    <pre>
                      {buildInstallerSnippet(installerRuntime, '___RAIOS_TOKEN___')
                        .split('___RAIOS_TOKEN___')
                        .map((part, i, arr) => (
                          <span key={i}>
                            {part}
                            {i < arr.length - 1 && (
                              <span className="tk">{`<${installerToken?.label ?? 'token'}>`}</span>
                            )}
                          </span>
                        ))}
                    </pre>
                  </div>

                  {/* Dica por runtime (onde o snippet vai) */}
                  <p style={{ fontSize: '.74rem', color: 'var(--wf-muted)', margin: '8px 0 0' }}>
                    {RUNTIME_HINTS[installerRuntime] ?? ''}
                  </p>

                  <div className="copy-inst">
                    <button
                      type="button"
                      className="dnewbtn"
                      style={{ marginLeft: 0 }}
                      onClick={handleCopyInstaller}
                      disabled={!installerToken || installerCopying}
                    >
                      {installerCopying ? (
                        <Loader2 className="ic animate-spin" aria-hidden />
                      ) : installerCopied ? (
                        <Check className="ic" aria-hidden />
                      ) : (
                        <Copy className="ic" aria-hidden />
                      )}
                      {installerCopied ? 'Copiado' : 'Copiar instalador'}
                    </button>
                    <span style={{ fontSize: '.76rem', color: 'var(--wf-muted)' }}>
                      Para <b style={{ color: 'var(--wf-text2)' }}>{installerRuntimeLabel}</b> · o token aparece como{' '}
                      <code>&lt;nome&gt;</code> no documento, mas a cópia leva o valor real.
                    </span>
                  </div>
                </div>
              )}

              <p style={{ fontSize: '.74rem', color: 'var(--wf-muted)', marginTop: 14 }}>
                Segurança: o snippet exibido nunca mostra o token completo — só o nome entre <code>&lt;...&gt;</code>.
                Ao clicar em <strong>Copiar instalador</strong>, o clipboard recebe o JSON com o token real no header{' '}
                <code>Authorization: Bearer</code>.
              </p>
            </div>
          )}

          {/* ─── Aba Tokens (MCP-03 — wireframe .tok-list/.tokc) ───────── */}
          {tab === 'tokens' && (
            <div>
              <div className="dhead">
                <button type="button" className="dnewbtn" onClick={openGenModal}>
                  <Plus className="ic" aria-hidden />
                  Gerar novo token
                </button>
              </div>

              {mcpLoading ? (
                <div className="tok-list">{[1, 2].map((i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
              ) : mcpTokens.length === 0 ? (
                <Card className="p-6">
                  <EmptyState
                    icon={KeyRound}
                    title="Nenhum token MCP"
                    description="Gere um token para conectar um runtime externo ao seu RAIOS."
                    action={{ label: 'Gerar novo token', onClick: openGenModal }}
                  />
                </Card>
              ) : (
                <div className="tok-list">
                  {mcpTokens.map((t) => (
                    <div key={t.id} className="tokc">
                      <div className="tok-ic">
                        <KeyRound className="ic" aria-hidden />
                      </div>
                      <div className="tok-main">
                        <div className="tok-name">{t.label}</div>
                        <div className="tok-meta">
                          <span>
                            runtime <b>{runtimeLabel(t.client_type)}</b>
                          </span>
                          <span>
                            início <b>{t.token_prefix}…</b>
                          </span>
                          <span>
                            último uso{' '}
                            <b>
                              {t.last_used_at
                                ? new Date(t.last_used_at).toLocaleString('pt-BR')
                                : 'nunca usado'}
                            </b>
                          </span>
                        </div>
                      </div>
                      <div className="tok-acts">
                        <button
                          type="button"
                          onClick={() => handleCopyMcp(t.id)}
                          title="Copiar token (valor completo, sem exibir)"
                          aria-label="Copiar token"
                        >
                          {mcpCopiedId === t.id ? (
                            <Check className="ic" aria-hidden />
                          ) : (
                            <Copy className="ic" aria-hidden />
                          )}
                        </button>
                        <button
                          type="button"
                          className="dl"
                          onClick={() => setRevokeConfirmId(t.id)}
                          disabled={mcpRevokingId === t.id}
                          title="Excluir token"
                          aria-label="Excluir token"
                        >
                          {mcpRevokingId === t.id ? (
                            <Loader2 className="ic animate-spin" aria-hidden />
                          ) : (
                            <Trash2 className="ic" aria-hidden />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal "Gerar novo token" (wireframe dlgNewToken) — valor exibido UMA vez */}
      {genModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="gen-token-title"
        >
          <Card className="w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 id="gen-token-title" className="text-lg font-semibold text-raios-text">
                Gerar novo token MCP
              </h3>
              <button type="button" onClick={closeGenModal} className="text-raios-muted hover:text-raios-text" aria-label="Fechar">
                <X className="h-4 w-4" />
              </button>
            </div>

            {!mcpJustCreated ? (
              <>
                <p className="text-sm text-raios-muted">
                  O token é exibido uma única vez na geração. Depois só o prefixo fica visível.
                </p>

                <Input
                  label="Nome do token"
                  value={mcpGenLabel}
                  onChange={(e) => setMcpGenLabel(e.target.value)}
                  placeholder="ex: token-openclaw-prod"
                />

                <div>
                  <label htmlFor="gen-runtime" className="block text-xs font-medium text-raios-text-secondary mb-1.5">
                    Runtime de destino
                  </label>
                  <select
                    id="gen-runtime"
                    value={mcpGenRuntime}
                    onChange={(e) => setMcpGenRuntime(e.target.value)}
                    className="w-full bg-raios-card border border-raios-border rounded-md text-sm text-raios-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-raios-accent/40"
                  >
                    {MCP_RUNTIMES.map((r) => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button variant="brand" onClick={handleGenerateMcp} loading={mcpGenerating} className="flex-1">
                    Gerar token
                  </Button>
                  <Button variant="outline" onClick={closeGenModal} disabled={mcpGenerating}>
                    Cancelar
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Alert variant="success">
                  Token <strong>{mcpJustCreated.label}</strong> criado. Copie agora — este valor não será exibido de novo.
                </Alert>

                <pre className="text-[11px] bg-raios-bg border border-raios-border rounded-md p-3 overflow-x-auto text-raios-text break-all whitespace-pre-wrap select-all">
                  {mcpJustCreated.token}
                </pre>

                <div className="flex gap-2 pt-1">
                  <Button
                    variant="brand"
                    onClick={() => handleCopyMcp('new', mcpJustCreated.token)}
                    className="flex-1"
                  >
                    {mcpCopiedId === 'new' ? <><Check className="h-3.5 w-3.5" /> Copiado</> : <><Copy className="h-3.5 w-3.5" /> Copiar token</>}
                  </Button>
                  <Button variant="outline" onClick={closeGenModal}>
                    Fechar
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* Confirmação de exclusão (wireframe dlgDelToken) */}
      {revokeConfirmToken && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
        >
          <Card className="w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-raios-text">Excluir token</h3>
            <p className="text-sm text-raios-text-secondary">
              Tem certeza que deseja excluir <strong>{revokeConfirmToken.label}</strong>?
              Runtimes que o usam perdem o acesso imediatamente.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setRevokeConfirmId(null)}>
                Cancelar
              </Button>
              <Button
                variant="outline"
                className="text-raios-danger-text"
                onClick={() => handleRevokeMcp(revokeConfirmToken.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Excluir
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
